/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Icon from "uis/Icon";
import { theme } from "styles/theme";
import TranslationBtn from "uis/translation-btn";
import { THomeSetting } from "apis/visitors/device";
import { TWorkspace } from "types/user.types";
import { stringToHtml } from "pages/visitors/visit-guide-manage/view-visit-guide";
import CheckIn from "./checkin";
import CheckInCheckOut from "./checkin-checkout";
import { useEffect, useState } from "react";
import { getWorkspacesEntryLogging } from "apis/workspace/visitors";

const Frame = styled.div`
  width: 450px;
  height: 720px;
  background-color: white;
  border-radius: 20px;
  border: 10px solid black;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div<{ backgroundColor: string; isHorizontal?: boolean }>`
  background-color: ${(props) => props.backgroundColor};
  height: ${(props) => (props.isHorizontal ? "180px" : "225px")};
  border-radius: ${(props) => (props.isHorizontal ? "0 0 60px 0" : "0 0 60px 60px")};
  position: relative;
  display: ${(props) => (props.isHorizontal ? "flex" : "block")};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 400px;
  justify-content: end;
  gap: 12px;
  padding: 30px 24px 24px 24px;
`;

const Title = styled.p<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 23px;
  font-weight: 400;
  line-height: 140%;
  padding: 40px 24px 20px 24px;
  overflow: hidden;
`;

const LogoContainer = styled.div<{ image?: string; isHorizontal?: boolean }>`
  background-color: white;
  background-image: ${(props) => (props.image ? `url("${props.image}")` : "none")};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  border-radius: 35px;
  box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.2);
  min-width: 110px;
  height: 110px;
  position: ${(props) => (props.isHorizontal ? "none" : "absolute")};
  left: 165px;
  ${(props) => (props.isHorizontal ? "margin: auto 24px auto auto" : "")};
`;

const DoorBellContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 62.5px;
  background: #ffc700;
  padding: 4px 20px 4px 15px;
  width: max-content;
  margin-left: auto;

  color: #fff;
  text-shadow: 0px 4px 20px rgba(255, 122, 0, 0.5);
  font-size: 16px;
  font-weight: 700;
`;

const InfoMessageTitle = styled.p<{ color: string }>`
  display: flex;
  gap: 4px;
  color: ${(props) => props.color};
  font-size: 16px;
  font-weight: 700;
  align-items: center;
`;

const InfoMessage = styled.div`
  height: 50px;
  margin-bottom: auto;
  border-radius: 10px;
  background-color: white;
  box-shadow: 0px 4px 26px 0px rgba(69, 76, 83, 0.08);
  padding: 10px 15px;
  font-size: 15px;
  color: ${theme.neutral500};
  overflow: hidden;
`;

export const getTextColorByBackgroundColor = (hexColor: string) => {
  const c = hexColor.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luma < 127.5 ? "white" : "black";
};

export default function Preview({
  settingInfo,
  workspace,
  primary,
  lang,
}: {
  settingInfo: THomeSetting;
  workspace: TWorkspace;
  primary: string;
  lang: "eng" | "kor";
}) {
  const [isEntryLoggingEnabled, setIsEntryLoggingEnabled] = useState<boolean>(false);

  useEffect(() => {
    getWorkspacesEntryLogging().then((res) => {
      setIsEntryLoggingEnabled(res?.data ?? false);
    });
  }, []);

  return (
    <Frame>
      <Header backgroundColor={primary} isHorizontal={settingInfo.isInfoMessageEnabled}>
        <Title color={getTextColorByBackgroundColor(primary)}>
          {stringToHtml(lang === "kor" ? settingInfo.greetingInKorFormat : settingInfo.greetingInEngFormat)}
        </Title>
        <LogoContainer image={workspace.image} isHorizontal={settingInfo.isInfoMessageEnabled} />
      </Header>

      <Container>
        {settingInfo.isInfoMessageEnabled && (
          <>
            <InfoMessageTitle color={primary}>
              <ChatIcon color={primary} /> {lang === "kor" ? " 안내사항" : " info"}
            </InfoMessageTitle>
            <InfoMessage>
              {lang === "kor"
                ? stringToHtml(settingInfo.infoMessageInKorFormat)
                : stringToHtml(settingInfo.infoMessageInEngFormat)}
            </InfoMessage>
          </>
        )}

        {settingInfo.isDoorBellEnabled ? (
          <DoorBellContainer>
            <BellIcon />
            <p>{lang === "kor" ? "초인종" : "Bell"}</p>
          </DoorBellContainer>
        ) : (
          <div
            css={css`
              height: 45px;
            `}
          ></div>
        )}
        {!isEntryLoggingEnabled ? <CheckIn lang={lang} settingInfo={settingInfo} /> : <CheckInCheckOut lang={lang} />}

        <TranslationBtn
          iconSize={24}
          styles={{
            container: css`
              padding: 6px 12px;
              width: max-content;
              margin-left: auto;
              border-radius: 100px;
              margin-top: 12px;
              gap: 4px;
            `,
            text: css`
              font-size: 16px;
            `,
          }}
        />
      </Container>
    </Frame>
  );
}

export const BellIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="26" viewBox="0 0 116 95" fill="none">
      <g filter="url(#filter0_d_3749_6954)">
        <path
          d="M51.6973 64.2571C51.6973 67.7303 54.5192 70.548 58.0036 70.548V63.5908H51.6973V64.2571Z"
          fill="white"
        />
        <path
          d="M54.9177 19.169V21.8502C46.613 23.3085 40.2986 30.5359 40.2986 39.2377V56.6412C38.7803 56.7616 37.584 58.0273 37.584 59.5739C37.584 61.1205 38.7803 62.3862 40.2986 62.5066V62.5173H58.0025V16.0918C56.2992 16.0918 54.9177 17.4698 54.9177 19.169Z"
          fill="white"
        />
        <path
          d="M24.8183 54.4406C25.4582 55.1382 26.547 55.19 27.249 54.5584C27.9557 53.9268 28.0082 52.8521 27.3683 52.1593C24.6464 49.1944 23.2615 45.3529 23.4621 41.3417C23.6626 37.3305 25.4295 33.6493 28.4333 30.9625C29.14 30.3309 29.1925 29.2563 28.5526 28.5634C28.2375 28.2146 27.8029 28.026 27.3636 28.0025C26.9242 27.9789 26.4753 28.125 26.122 28.4408C22.4353 31.7356 20.2721 36.2605 20.0238 41.1767C19.7754 46.093 21.4803 50.8018 24.8183 54.4406Z"
          fill="white"
        />
        <path
          d="M33.5451 33.5772C33.2127 33.2163 32.7662 33.0212 32.3097 33.0017C31.8532 32.9822 31.3868 33.1285 31.0197 33.4552C28.5984 35.6107 27.1794 38.5611 27.0157 41.7651C26.852 44.9691 27.9733 48.0414 30.1613 50.4212C30.8262 51.143 31.9574 51.1966 32.6868 50.5431C33.4161 49.8897 33.4757 48.7778 32.8108 48.0609C31.2628 46.3785 30.469 44.2035 30.5831 41.9407C30.6972 39.6779 31.7044 37.5907 33.4112 36.0643C34.1455 35.4108 34.2 34.2989 33.5352 33.582L33.5451 33.5772Z"
          fill="white"
        />
        <path
          d="M90.6464 28.5594C89.9354 27.8618 88.7256 27.81 87.9456 28.4416C87.1603 29.0732 87.102 30.1479 87.813 30.8407C90.8374 33.8056 92.3761 37.6471 92.1533 41.6583C91.9304 45.6695 89.9672 49.3507 86.6297 52.0375C85.8444 52.6691 85.7861 53.7437 86.4971 54.4366C86.8473 54.7854 87.3301 54.974 87.8183 54.9975C88.3064 55.0211 88.8052 54.875 89.1978 54.5592C93.2941 51.2644 95.6977 46.7395 95.9736 41.8233C96.2495 36.907 94.3553 32.1982 90.6464 28.5594Z"
          fill="white"
        />
        <path
          d="M82.3899 50.3908C82.6748 50.7717 83.0576 50.9776 83.4488 50.9982C83.8401 51.0188 84.2398 50.8643 84.5546 50.5195C86.6299 48.2442 87.8462 45.1299 87.9865 41.7479C88.1269 38.3659 87.1658 35.123 85.2903 32.6109C84.7204 31.8491 83.7508 31.7925 83.1256 32.4822C82.5005 33.172 82.4494 34.3457 83.0193 35.1024C84.3462 36.8783 85.0266 39.1741 84.9288 41.5626C84.831 43.9511 83.9677 46.1543 82.5047 47.7655C81.8753 48.4553 81.8285 49.6289 82.3984 50.3856L82.3899 50.3908Z"
          fill="white"
        />
        <path
          d="M64.1267 64.2835V63.6172H57.8203V70.5743C61.3021 70.5743 64.1267 67.7594 64.1267 64.2835Z"
          fill="white"
        />
        <path
          d="M75.5242 56.6705V39.267C75.5242 30.5625 69.2125 23.3351 60.9051 21.8795V19.1983C60.9051 17.4991 59.5236 16.1211 57.8203 16.1211V62.5466H75.5242V62.5359C77.0425 62.4155 78.2388 61.1498 78.2388 59.6032C78.2388 58.0566 77.0425 56.7909 75.5242 56.6705Z"
          fill="white"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_3749_6954"
          x="-4"
          y="-9"
          width="124"
          height="114"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="10" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0.48 0 0 0 0 0 0 0 0 0.5 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3749_6954" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3749_6954" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

const ChatIcon = ({ color }: { color: string }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 30 30" fill="none">
      <g clip-path="url(#clip0_87_134)">
        <path
          d="M3.75 0C1.68164 0 0 1.68164 0 3.75V20.625C0 22.6934 1.68164 24.375 3.75 24.375H9.375L22.5 29.5C22.5 29.5 24.5 30 24 29.5C24 29.5 23.9002 29.4731 23.5 29L21 24.5L26.25 24.375C28.3184 24.375 30 22.6934 30 20.625V3.75C30 1.68164 28.3184 0 26.25 0H3.75Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="clip0_87_134">
          <rect width="30" height="30" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
